












































































































































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'
import { RealworksStatusRule } from '@/api/model/realworks-status-rule'
import { RealworksKenmerkAPI, RealworksStatusRuleAPI } from '@/api/backend-client'
import { RealworksAPI } from '@/api/client/realworks'
import { Pipeline } from '@/api/model/pipeline'
import { PipelineAPI } from '@/api/backend-client'
import { Status } from '@/api/model/status'
import { StatusAPI } from '@/api/backend-client'
import { StatusReason } from '@/api/model/status-reason'
import { StatusReasonAPI } from '@/api/backend-client'
import { ReferralStatus, ReferralType, allowedReferralStatusReasons } from '@/api/types'
import { generateReferralStatusOptions, InputSelectOption } from '@/helpers/options'
import { escapeHtml } from '@/helpers/html'

@Component
export default class EditRealworksStatusRuleForm extends Vue {
    @Prop({ required: true }) readonly value!: RealworksStatusRule
    @Prop({ required: true }) readonly create: boolean
    realworksStatusRule: RealworksStatusRule = null
    submitting: boolean = false
    triggerType: string = null
    loadingKenmerken: boolean = true
    kenmerken: InputSelectOption[] = []
    loadingAgendaTypes: boolean = true
    agendaTypes: InputSelectOption[] = []
    loadingAgendaStatussen: boolean = true
    agendaStatussen: InputSelectOption[] = []
    loadingTaakTypes: boolean = true
    taakTypes: InputSelectOption[] = []
    loadingTaakStatussen: boolean = true
    taakStatussen: InputSelectOption[] = []
    loadingPipelines: boolean = true
    pipelines: Pipeline[] = []
    loadingNewStatuses: boolean = true
    newStatuses: Status[] = []
    loadingNewStatusReasons: boolean = true
    newStatusReasons: StatusReason[] = []

    mounted() {
        this.loadKenmerken()
        this.loadAgendaTypes()
        this.loadAgendaStatussen()
        this.loadTaakTypes()
        this.loadTaakStatussen()
        this.loadPipelines()
        this.loadNewStatuses()
        this.loadNewStatusReasons()
    }

    @Watch('value', { immediate: true })
    onValueChanged(): void {
        this.realworksStatusRule = cloneDeep(this.value)
        this.triggerType = this.realworksStatusRule.triggerType
    }

    @Watch('triggerType', { immediate: true })
    onTriggerTypeChanged(): void {
        if (this.triggerType !== 'kenmerken') {
            this.realworksStatusRule.kenmerken = []
        }

        if (this.triggerType !== 'agendaTypes') {
            this.realworksStatusRule.agendaTypes = []
            this.realworksStatusRule.agendaStatus = null
        }

        if (this.triggerType !== 'taakTypes') {
            this.realworksStatusRule.taakTypes = []
            this.realworksStatusRule.taakStatus = null
        }
    }

    @Watch('realworksStatusRule.toStatus')
    onToStatusChanged(toStatus): void {
        this.realworksStatusRule.toStatusReason = null
        this.realworksStatusRule.toNewStatusReason = null
        this.realworksStatusRule.toPhase = null
    }

    @Watch('triggerType')
    updateAssignToMedewerker(): void {
        if (!this.supportsAssignToMedewerker) {
            this.realworksStatusRule.assignToMedewerker = false
        } else {
            this.realworksStatusRule.assignToMedewerker = true
        }
    }

    @Watch('triggerType')
    updateApplyToTriggeredReferrals(): void {
        if (!this.supportsTriggeredReferrals) {
            this.realworksStatusRule.applyToTriggeredReferrals = false
        }
    }

    async saveRealworksStatusRule(): Promise<void> {
        try {
            this.submitting = true

            if (!this.create) {
                await RealworksStatusRuleAPI.update(this.realworksStatusRule.id, this.realworksStatusRule)
            } else if (this.create) {
                await RealworksStatusRuleAPI.create(this.realworksStatusRule)
            }

            await this.$emit('save')
        } catch (error) {
            this.$notify({
                type: 'error',
                title: 'Foutmelding',
                text: escapeHtml(error.message),
            })
        } finally {
            this.submitting = false
        }
    }

    get triggerTypeOptions() {
        return [
            { label: this.$t('forms.edit_realworks_status_rule_form.triggerTypes.kenmerken'), value: 'kenmerken' },
            { label: this.$t('forms.edit_realworks_status_rule_form.triggerTypes.agendaTypes'), value: 'agendaTypes' },
            { label: this.$t('forms.edit_realworks_status_rule_form.triggerTypes.taakTypes'), value: 'taakTypes' },
        ]
    }

    async loadKenmerken() {
        this.loadingKenmerken = true

        const kenmerken = await RealworksKenmerkAPI.getAll()
        this.kenmerken = kenmerken
            .map(kenmerk => ({
                label: kenmerk.omschrijving,
                value: kenmerk.id.toString(), // cast to string because API expects a string
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingKenmerken = false
    }

    async loadAgendaTypes() {
        this.loadingAgendaTypes = true

        const agendaTypes = await RealworksAPI.getAgendatypes()
        this.agendaTypes = agendaTypes
            .map(agendatype => ({
                label: agendatype,
                value: agendatype,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingAgendaTypes = false
    }

    async loadAgendaStatussen() {
        this.loadingAgendaStatussen = true

        const agendaStatussen = await RealworksAPI.getAgendaStatussen()
        this.agendaStatussen = agendaStatussen
            .filter(status => status.actief)
            .sort((a, b) => a.volgorde > b.volgorde ? 1 : -1)
            .map(status => ({
                label: status.status,
                value: status.status,
            }))

        this.agendaStatussen.unshift({
            label: this.$t('forms.edit_realworks_status_rule_form.emptyAgendaStatus').toString(),
            value: null,
        })

        this.loadingAgendaStatussen = false
    }

    async loadTaakTypes() {
        this.loadingTaakTypes = true

        const taakTypes = await RealworksAPI.getTaaktypes()
        this.taakTypes = taakTypes
            .map(taak => ({
                label: taak,
                value: taak,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingTaakTypes = false
    }

    async loadTaakStatussen() {
        this.loadingTaakStatussen = true

        const taakStatussen = await RealworksAPI.getTaakStatussen()
        this.taakStatussen = taakStatussen
            .filter(status => status.actief)
            .sort((a, b) => a.volgorde > b.volgorde ? 1 : -1)
            .map(status => ({
                label: status.status,
                value: status.status,
            }))

        this.taakStatussen.unshift({
            label: this.$t('forms.edit_realworks_status_rule_form.emptyTaakStatus').toString(),
            value: null,
        })

        this.loadingTaakStatussen = false
    }

    async loadPipelines(): Promise<void> {
        this.loadingPipelines = true
        this.pipelines = await PipelineAPI.getAll({ company: this.$currentCompany.id })
        this.loadingPipelines = false
    }

    async loadNewStatuses(): Promise<void> {
        this.loadingNewStatuses = true
        this.newStatuses = await StatusAPI.getAll({ company: this.$currentCompany.id })
        this.loadingNewStatuses = false
    }

    async loadNewStatusReasons(): Promise<void> {
        this.loadingNewStatusReasons = true
        this.newStatusReasons = await StatusReasonAPI.getAll({ company: this.$currentCompany.id })
        this.loadingNewStatusReasons = false
    }

    get referralTypes(): InputSelectOption[] {
        return this.$currentCompany.handledReferralTypes
            .map((type: string) => ({
                label: this.$t(`referral.type.${type}`) as string,
                value: type,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get statuses(): InputSelectOption[] {
        return generateReferralStatusOptions()
    }

    get statusReasons(): InputSelectOption[] {
        return this.allowedStatusReasons.map(statusReason => {
            return {
                label: this.$t(`referral.statusReason.${statusReason}`) as string,
                value: statusReason,
            }
        })
    }

    get pipeline(): string|null {
        return this.realworksStatusRule?.pipeline?.id || null
    }

    set pipeline(id: string|null) {
        this.realworksStatusRule.pipeline = this.pipelines
            .find(v => v.id === id) || null
    }

    get pipelineOptions(): InputSelectOption[] {
        return this.pipelines
            .map(pipeline => ({
                label: pipeline.name,
                value: pipeline.id,
            }))
    }

    get phases(): string[] {
        const phases = this.realworksStatusRule?.phases || []

        return phases.map(phase => phase.id)
    }

    set phases(ids: string[]) {
        const phases = this.realworksStatusRule?.pipeline?.phases || []

        this.realworksStatusRule.phases = phases.filter(phase => ids.includes(phase.id))
    }

    get phasesOptions(): InputSelectOption[] {
        const phases = this.realworksStatusRule?.pipeline?.phases || []

        return phases
            .map(phase => ({
                label: phase.name,
                value: phase.id,
            }))
    }

    get allowedStatusReasons() {
        if (!this.realworksStatusRule) {
            return []
        }

        let allowedStatusReasons = []

        this.realworksStatusRule.statuses.forEach(status => {
            allowedStatusReasons = [].concat(
                allowedStatusReasons,
                allowedReferralStatusReasons(
                    'all' as ReferralType,
                    status as ReferralStatus,
                ),
            )
        })

        return allowedStatusReasons
    }

    @Watch('allowedStatusReasons')
    updateStatusReasons() {
        if (!this.realworksStatusRule) {
            return
        }

        this.realworksStatusRule.statusReasons = this.realworksStatusRule.statusReasons.filter(statusReason => {
            return this.allowedStatusReasons.includes(statusReason)
        })
    }

    get toStatuses() {
        return generateReferralStatusOptions(this.$currentCompany)
    }

    get toStatusReasons() {
        if (!this.realworksStatusRule || !this.realworksStatusRule.toStatus || this.$currentCompany.pipelinesEnabled) {
            return []
        }

        const allowedStatusReasons = allowedReferralStatusReasons(
            'all' as ReferralType,
            this.realworksStatusRule.toStatus as ReferralStatus,
        )

        return allowedStatusReasons.map(statusReason => {
            return {
                label: this.$t(`referral.statusReason.${statusReason}`),
                value: statusReason,
            }
        })
    }

    get toNewStatus(): Status|null {
        if (!this.realworksStatusRule || !this.realworksStatusRule.toStatus || !this.$currentCompany.pipelinesEnabled) {
            return null
        }

        return this.newStatuses.find((status: Status) => status.status === this.realworksStatusRule.toStatus)
    }

    get toNewStatusReasons(): StatusReason[] {
        if (!this.realworksStatusRule || !this.realworksStatusRule.toStatus || !this.$currentCompany.pipelinesEnabled) {
            return []
        }

        return this.newStatusReasons.filter((statusReason: StatusReason) => statusReason.status === this.realworksStatusRule.toStatus)
    }

    get toNewStatusReason(): string|null {
        return this.realworksStatusRule?.toNewStatusReason?.id || null
    }

    set toNewStatusReason(id: string|null) {
        this.realworksStatusRule.toNewStatusReason = this.toNewStatusReasons
            .find(v => v.id === id) || null
    }

    get toNewStatusReasonOptions(): InputSelectOption[] {
        if (!this.realworksStatusRule?.toStatus || !this.realworksStatusRule?.pipeline) {
            return []
        }

        let options = this.toNewStatusReasons
            .filter(statusReason => statusReason.status === this.realworksStatusRule.toStatus)
            .filter(statusReason => statusReason.pipeline.id === this.pipeline)

        if (this.realworksStatusRule.toStatus === 'open') {

            if (!this.realworksStatusRule.toPhase) {
                return []
            }

            options = options
                .filter(statusReason => {
                    return statusReason
                        .phases
                        .map(phase => phase.id)
                        .includes(this.realworksStatusRule.toPhase.id)
            })
        }

        return options.map(statusReason => ({
            label: statusReason.name,
            value: statusReason.id,
        }))
    }

    get toNewStatusReasonRequired(): boolean {
        if (this.toNewStatus) {
            return this.toNewStatus.statusReasonRequired

        }
        return false
    }

    get toPhase(): string|null {
        return this.realworksStatusRule?.toPhase?.id || null
    }

    set toPhase(id: string|null) {
        const phases = this.realworksStatusRule?.pipeline?.phases || []

        this.realworksStatusRule.toPhase = phases
            .find(v => v.id === id) || null
    }

    get yesNoOptions() {
        return [
            { label: this.$t('yes'), value: true },
            { label: this.$t('no'), value: false },
        ]
    }

    get supportsAssignToMedewerker(): boolean {
        return ['agendaTypes'].includes(this.triggerType)
    }

    get supportsTriggeredReferrals(): boolean {
        return true
    }

    get supportsTriggeredReferralsPastDays(): boolean {
        return ['agendaTypes', 'taakTypes'].includes(this.triggerType)
    }
}
